import {
  Location,
  Redirect,
  RouteComponentProps,
  Router,
  WindowLocation,
} from "@reach/router";
import { AppPath, paths } from "appConstants";
import ErrorBoundary from "components/ErrorBoundary";
import ErrorFallback from "components/fallbacks/ErrorFallback";
import Page from "components/Page";
import { ReviewVideoLocationProps } from "components/ReviewVideo/models";
import { RightPanelRouter } from "components/rightPanel/RightPanelRouter";
import { Spinner } from "components/Spinner/Spinner";
import { Toasts } from "components/Toasts";

import { endpoints } from "config";
import { useCheckSignedIn } from "helpers/useCheckSignedIn";
import { registerUserLocale } from "helpers/userLocale";
import { useSignOutAcrossTabs } from "helpers/useSignOutAcrossTabs";
import AdminUsers from "pages/AdminUsers";
import React, { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { selectAppVisible } from "reducers/auth";
import { FooterType } from "reducers/footer";
import { StringLiteralLike } from "typescript";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "components/Meetings/authConfig";

type ReviewFeedbackPageProps = {
  endpoint: string;
  saveEndpoint: string;
  saveNoteEndpoint: string;
  heading: string;
  subHeading: string;
  isClientFeedback?: boolean;
};

/** Standard Components */
// TODO Remove
const SignInForm = lazy(() => import("components/SignIn/SignInForm"));

const ForgottenPassword = lazy(() =>
  import("components/ForgottenPassword/ForgottenPassword")
);
const ResetPassword = lazy(() =>
  import("components/ResetPassword/ResetPassword")
);
const SetPassword = lazy(() => import("components/SetPassword/SetPassword"));
const NotFound = lazy(() => import("pages/NotFound"));
const Home = lazy(() => import("pages/Home"));
const Dashboard = lazy(() =>
  import("components/Dashboard/components/Dashboard")
);

const PrivacyPolicy = lazy(() =>
  import("components/privacyPolicy/PrivacyPolicy")
);
const TermsAndConditions = lazy(() =>
  import("components/termsAndConditions/TermsAndConditions")
);
const StaticContents = lazy(() => import("pages/staticContents"));

const RecordedInterview = lazy(() =>
  import("components/RecordedInterview/RecordedInterview")
);
const PresentationActivity = lazy(() =>
  import("components/PresentationActivity/PresentationActivity")
);
const BusinessCaseActivity = lazy(() =>
  import("components/businessCaseActivity/BusinessCaseActivity")
);
const VirtualMeeting = lazy(() =>
  import("components/VirtualMeeting/VirtualMeeting")
);
const VirtualTeamDiscussion = lazy(() =>
  import("components/VirtualTeamDiscussion/VirtualTeamDiscussion")
);
const LiveInterview = lazy(() =>
  import("components/LiveInterview/LiveInterview")
);
const ReviewAssessment = lazy(() =>
  import("components/ReviewAssessment/ReviewAssessment")
);
const ReviewFeedback = lazy(() =>
  import("components/ReviewFeedback/ReviewFeedback")
);
const Meetings = lazy(() => import("components/Meetings/components/Meetings"));
const Messages = lazy(() => import("./pages/messages/MessagesMain"));

const ReviewVideo = lazy(() => import("components/ReviewVideo/ReviewVideo"));
export type PageProps = RouteComponentProps & {
  navType: string;
  navData: { title: string } | null;
  footerType: FooterType | null;
  location: WindowLocation;
  children?: any;
};

const NotFoundPage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <NotFound {...props} />
  </Page>
);
const StaticContentsPage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <StaticContents {...props} />
  </Page>
);
const SetPasswordPage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <SetPassword {...props} />
  </Page>
);

const ResetPasswordPage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <ResetPassword location={props.location!} />
  </Page>
);

const HomePage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <Home {...props} />
  </Page>
);

const DashboardPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <Dashboard {...props} />
  </Page>
);
const MeetingsPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <Meetings {...props} />
  </Page>
);

const RecordedInterviewRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const PresentationActivityRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const BusinessCaseActivityRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const VirtualTeamDiscussionRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const VirtualMeetingRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const LiveInterviewRoute = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <div style={{ width: "100%" }}>{props.children}</div>
  </Page>
);
const RecordedInterviewPage = (props: PageProps & { endpoint: string }) => (
  <RecordedInterview {...props} />
);
const PresentationActivityPage = (props: PageProps & { endpoint: string }) => (
  <PresentationActivity {...props} />
);
const BusinessCaseActivityPage = (props: PageProps & { endpoint: string }) => (
  <BusinessCaseActivity {...props} />
);
const VirtualTeamDiscussionPage = (props: PageProps & { endpoint: string }) => (
  <VirtualTeamDiscussion {...props} />
);
const VirtualMeetingPage = (props: PageProps & { endpoint: string }) => (
  <VirtualMeeting {...props} />
);
const LiveInterviewPage = (props: PageProps & { endpoint: string }) => (
  <LiveInterview {...props} />
);
const ReviewAssessmentPage = (
  props: PageProps & { endpoint: string; saveNoteEndpoint: string }
) => <ReviewAssessment {...props} />;
const ReviewFeedbackPage = (props: PageProps & ReviewFeedbackPageProps) => (
  <ReviewFeedback {...props} />
);
const ReviewVideoPage = (
  props: PageProps & RouteComponentProps<ReviewVideoLocationProps>
) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <ReviewVideo {...props} />
  </Page>
);

const ForgottenPasswordPage = (props: PageProps) => (
  <Page
    isPublic
    navType={props.navType}
    navData={null}
    footerType={props.footerType}
    location={props.location}
  >
    <ForgottenPassword {...props} />
  </Page>
);

const SignInPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={null}
    isPublic
    footerType={props.footerType}
    location={props.location}
  >
    <SignInForm {...props} />
  </Page>
);

const PrivacyPolicyPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={null}
    isPublic
    footerType={props.footerType}
    location={props.location}
  >
    <PrivacyPolicy {...props} />
  </Page>
);

const TermsAndConditionsPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={null}
    isPublic
    footerType={props.footerType}
    location={props.location}
  >
    <TermsAndConditions {...props} />
  </Page>
);

const MessagesPage = (props: PageProps) => (
  <Page
    navType={props.navType}
    navData={props.navData}
    footerType={props.footerType}
    location={props.location}
  >
    <Messages {...props} />
  </Page>
);

// Array of strings represeting the paths where nav should be hidden.
const HIDE_NAV_ON_PATHS: string[] = [];

/* Main App */
const App: React.FC<RouteComponentProps> = ({ location }) => {
  registerUserLocale();
  const isSignedIn = useCheckSignedIn();
  const isAppVisible = useSelector(selectAppVisible);
  const pca = new PublicClientApplication(msalConfig);

  const pathname = location?.pathname;
  const currentLocationIncludes = (locationStrings: string[]): boolean => {
    if (pathname) {
      for (let i = 0; i < locationStrings.length; i++) {
        const current = locationStrings[i];
        if (pathname.includes(current)) {
          return true;
        }
      }
    }

    return false;
  };
  const navHidden = !isSignedIn || currentLocationIncludes(HIDE_NAV_ON_PATHS);

  // If we sign out here, we should sign out in other open windows.
  useSignOutAcrossTabs();

  // At startup, we check to see if we should display a signin page or the content.
  // Return nothing, so that we don't flash a view of signin if unneccessary.
  if (!isAppVisible) return null;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<Spinner positionAbsolute />}>
        <MsalProvider instance={pca}>
          <div className={navHidden ? "Main NoNav" : "Main"}>
            <Location>
              {({ location }) => (
                <>
                  <Router>
                    <Redirect
                      from={`${AppPath.ROOT}`}
                      to={`${AppPath.SIGN_IN}`}
                    />
                    <AdminUsers
                      path={paths.adminUsers.url}
                      navType={paths.adminUsers.navType}
                      navData={paths.adminUsers.data}
                      footerType={paths.adminUsers.footerType}
                      location={location}
                    />
                    <DashboardPage
                      path={paths.dashboard.url}
                      navType={paths.dashboard.navType}
                      navData={paths.dashboard.data}
                      footerType={paths.dashboard.footerType}
                      location={location}
                    />
                    <MeetingsPage
                      path={paths.meetings.url}
                      navType={paths.meetings.navType}
                      navData={paths.meetings.data}
                      footerType={paths.meetings.footerType}
                      location={location}
                    />
                    <RecordedInterviewRoute
                      path={paths.recordedInterviewRoute.url}
                      navType={paths.recordedInterviewRoute.navType}
                      navData={paths.recordedInterviewRoute.data}
                      footerType={paths.recordedInterviewRoute.footerType}
                      location={location}
                    >
                      <RecordedInterviewPage
                        path={paths.recordedInterview.url}
                        navType={paths.recordedInterview.navType}
                        navData={paths.recordedInterview.data}
                        footerType={paths.recordedInterview.footerType}
                        location={location}
                        endpoint={endpoints.recordedInterview.candidateToAssess}
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.recordedInterview.assessInterviewDetails
                        }
                        saveNoteEndpoint={endpoints.recordedInterview.saveNote}
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.recordedInterview.candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.recordedInterview.saveCandidateFeedback
                        }
                        saveNoteEndpoint={endpoints.recordedInterview.saveNote}
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.recordedInterview.assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.recordedInterview.saveAssessorFeedback
                        }
                        saveNoteEndpoint={endpoints.recordedInterview.saveNote}
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </RecordedInterviewRoute>
                    <PresentationActivityRoute
                      path={paths.presentationActivityRoute.url}
                      navType={paths.presentationActivityRoute.navType}
                      navData={paths.presentationActivityRoute.data}
                      footerType={paths.presentationActivityRoute.footerType}
                      location={location}
                    >
                      <PresentationActivityPage
                        path={paths.presentationActivity.url}
                        navType={paths.presentationActivity.navType}
                        navData={paths.presentationActivity.data}
                        footerType={paths.presentationActivity.footerType}
                        location={location}
                        endpoint={
                          endpoints.presentationActivity.candidateToAssess
                        }
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.presentationActivity.assessInterviewDetails
                        }
                        saveNoteEndpoint={
                          endpoints.presentationActivity.saveNote
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.presentationActivity
                            .candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.presentationActivity.saveCandidateFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.presentationActivity.saveNote
                        }
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.presentationActivity.assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.presentationActivity.saveAssessorFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.presentationActivity.saveNote
                        }
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </PresentationActivityRoute>
                    <VirtualTeamDiscussionRoute
                      path={paths.virtualTeamDiscussionRoute.url}
                      navType={paths.virtualTeamDiscussionRoute.navType}
                      navData={paths.virtualTeamDiscussionRoute.data}
                      footerType={paths.virtualTeamDiscussionRoute.footerType}
                      location={location}
                    >
                      <VirtualTeamDiscussionPage
                        path={paths.virtualTeamDiscussion.url}
                        navType={paths.virtualTeamDiscussion.navType}
                        navData={paths.virtualTeamDiscussion.data}
                        footerType={paths.virtualTeamDiscussion.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualTeamDiscussion.candidateToAssess
                        }
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualTeamDiscussion.assessInterviewDetails
                        }
                        saveNoteEndpoint={
                          endpoints.virtualTeamDiscussion.saveNote
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualTeamDiscussion
                            .candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.virtualTeamDiscussion.saveCandidateFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.virtualTeamDiscussion.saveNote
                        }
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualTeamDiscussion
                            .assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.virtualTeamDiscussion.saveAssessorFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.virtualTeamDiscussion.saveNote
                        }
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </VirtualTeamDiscussionRoute>
                    <VirtualMeetingRoute
                      path={paths.virtualMeetingRoute.url}
                      navType={paths.virtualMeetingRoute.navType}
                      navData={paths.virtualMeetingRoute.data}
                      footerType={paths.virtualMeetingRoute.footerType}
                      location={location}
                    >
                      <VirtualMeetingPage
                        path={paths.virtualMeeting.url}
                        navType={paths.virtualMeeting.navType}
                        navData={paths.virtualMeeting.data}
                        footerType={paths.virtualMeeting.footerType}
                        location={location}
                        endpoint={endpoints.virtualMeeting.candidateToAssess}
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualMeeting.assessInterviewDetails
                        }
                        saveNoteEndpoint={endpoints.virtualMeeting.saveNote}
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualMeeting.candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.virtualMeeting.saveCandidateFeedback
                        }
                        saveNoteEndpoint={endpoints.virtualMeeting.saveNote}
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.virtualMeeting.assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.virtualMeeting.saveAssessorFeedback
                        }
                        saveNoteEndpoint={endpoints.virtualMeeting.saveNote}
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </VirtualMeetingRoute>
                    <LiveInterviewRoute
                      path={paths.liveInterviewRoute.url}
                      navType={paths.liveInterviewRoute.navType}
                      navData={paths.liveInterviewRoute.data}
                      footerType={paths.liveInterviewRoute.footerType}
                      location={location}
                    >
                      <LiveInterviewPage
                        path={paths.liveInterview.url}
                        navType={paths.liveInterview.navType}
                        navData={paths.liveInterview.data}
                        footerType={paths.liveInterview.footerType}
                        location={location}
                        endpoint={endpoints.liveInterview.candidateToAssess}
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.liveInterview.assessInterviewDetails
                        }
                        saveNoteEndpoint={endpoints.liveInterview.saveNote}
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.liveInterview.candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.liveInterview.saveCandidateFeedback
                        }
                        saveNoteEndpoint={endpoints.liveInterview.saveNote}
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.liveInterview.assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.liveInterview.saveAssessorFeedback
                        }
                        saveNoteEndpoint={endpoints.liveInterview.saveNote}
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </LiveInterviewRoute>
                    <BusinessCaseActivityRoute
                      path={paths.businessCaseActivityRoute.url}
                      navType={paths.businessCaseActivityRoute.navType}
                      navData={paths.businessCaseActivityRoute.data}
                      footerType={paths.businessCaseActivityRoute.footerType}
                      location={location}
                    >
                      <BusinessCaseActivityPage
                        path={paths.businessCaseActivity.url}
                        navType={paths.businessCaseActivity.navType}
                        navData={paths.businessCaseActivity.data}
                        footerType={paths.businessCaseActivity.footerType}
                        location={location}
                        endpoint={
                          endpoints.businessCaseActivity.candidateToAssess
                        }
                      />
                      <ReviewAssessmentPage
                        path={paths.reviewAssessment.url}
                        navType={paths.reviewAssessment.navType}
                        navData={paths.reviewAssessment.data}
                        footerType={paths.reviewAssessment.footerType}
                        location={location}
                        endpoint={
                          endpoints.businessCaseActivity
                            .assessBusinessCaseDetails
                        }
                        saveNoteEndpoint={
                          endpoints.businessCaseActivity.saveNote
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackCandidate.url}
                        navType={paths.reviewFeedbackCandidate.navType}
                        navData={paths.reviewFeedbackCandidate.data}
                        footerType={paths.reviewFeedbackCandidate.footerType}
                        location={location}
                        endpoint={
                          endpoints.businessCaseActivity
                            .candidateFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.businessCaseActivity.saveCandidateFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.businessCaseActivity.saveNote
                        }
                        heading={
                          "Give Feedback which <span class='RedHighlight'>will be made available</span> to the Candidate"
                        }
                        subHeading={
                          "Please note that this feedback will be sent directly to the candidate and you should ensure you word it accordingly"
                        }
                      />
                      <ReviewFeedbackPage
                        path={paths.reviewFeedbackClient.url}
                        navType={paths.reviewFeedbackClient.navType}
                        navData={paths.reviewFeedbackClient.data}
                        footerType={paths.reviewFeedbackClient.footerType}
                        location={location}
                        endpoint={
                          endpoints.businessCaseActivity.assessorFeedbackDetails
                        }
                        saveEndpoint={
                          endpoints.businessCaseActivity.saveAssessorFeedback
                        }
                        saveNoteEndpoint={
                          endpoints.businessCaseActivity.saveNote
                        }
                        heading={
                          "Give Feedback that will not be made available to the Candidate"
                        }
                        subHeading={
                          "The feedback that you enter here will be available only to the recruitment team and will not be seen by the candidate"
                        }
                        isClientFeedback
                      />
                    </BusinessCaseActivityRoute>
                    <PrivacyPolicyPage
                      path={paths.privacyPolicyPage.url}
                      navType={paths.privacyPolicyPage.navType}
                      navData={paths.privacyPolicyPage.data}
                      footerType={paths.privacyPolicyPage.footerType}
                      location={location}
                    />
                    <StaticContentsPage
                      path={paths.staticContents.url}
                      navType={paths.staticContents.navType}
                      navData={paths.staticContents.data}
                      footerType={paths.staticContents.footerType}
                      location={location}
                    />
                    <SignInPage
                      path={paths.signInPage.url}
                      navType={paths.signInPage.navType}
                      navData={paths.signInPage.data}
                      footerType={paths.signInPage.footerType}
                      location={location}
                    />
                    <ForgottenPasswordPage
                      path={paths.forgottenPasswordPage.url}
                      navType={paths.forgottenPasswordPage.navType}
                      navData={paths.forgottenPasswordPage.data}
                      footerType={paths.forgottenPasswordPage.footerType}
                      location={location}
                    />
                    <ResetPasswordPage
                      path={paths.resetPasswordPage.url}
                      navType={paths.resetPasswordPage.navType}
                      navData={paths.resetPasswordPage.data}
                      footerType={paths.resetPasswordPage.footerType}
                      location={location}
                    />
                    <SetPasswordPage
                      path={paths.setPasswordPage.url}
                      navType={paths.setPasswordPage.navType}
                      navData={paths.setPasswordPage.data}
                      footerType={paths.setPasswordPage.footerType}
                      location={location}
                    />
                    <PrivacyPolicyPage
                      path={paths.privacyPolicyPage.url}
                      navType={paths.privacyPolicyPage.navType}
                      navData={paths.privacyPolicyPage.data}
                      footerType={paths.privacyPolicyPage.footerType}
                      location={location}
                    />
                    <TermsAndConditionsPage
                      path={paths.termsAndConditionsPage.url}
                      navType={paths.termsAndConditionsPage.navType}
                      navData={paths.termsAndConditionsPage.data}
                      footerType={paths.termsAndConditionsPage.footerType}
                      location={location}
                    />
                    <NotFoundPage
                      default
                      path={paths.notFoundPage.url}
                      navType={paths.notFoundPage.navType}
                      navData={paths.notFoundPage.data}
                      footerType={paths.notFoundPage.footerType}
                      location={location}
                    />
                    <ReviewVideoPage
                      path={paths.reviewVideo.url}
                      navType={paths.reviewVideo.navType}
                      navData={paths.reviewVideo.data}
                      footerType={paths.reviewVideo.footerType}
                      location={location as any}
                    />
                    <MessagesPage
                      path={paths.messages.url}
                      navType={paths.messages.navType}
                      footerType={paths.messages.footerType}
                      navData={paths.messages.data}
                      location={location}
                    />
                  </Router>
                  <RightPanelRouter />
                </>
              )}
            </Location>
          </div>
        </MsalProvider>
      </Suspense>

      <Toasts />
    </ErrorBoundary>
  );
};

export default App;
